import React, {useState, useContext, useEffect} from "react";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {get, filter} from "lodash";
import {MultiSelectAutocomplete} from "../../../Components/Form/AutoComplete/AutoComplete";
import './OrganizationFilter.scss';

/**
 * Renders an organization filter component for data grids.
 * Supports both single-select (legacy) and multi-select modes.
 *
 * NOTE: This component updates the parent immediately on selection change.
 * Debouncing should be handled by the parent component if needed (e.g., DataGridWrapper uses useDebounce).
 *
 * @param {object} params - The function parameters container object.
 * @param {object} params.filterModel - The current filter model object, containing filter values and data.
 * @param {function} params.setFilterModel - Function to update the filter model.
 * @param {boolean} [params.enableMultiSelect=true] - Enable multi-select mode for organizations.
 * @param {boolean} [params.enableSelectAll=true] - Enable "Select All" option in multi-select mode.
 */
export const OrganizationFilter = ({filterModel, setFilterModel, enableMultiSelect = true, enableSelectAll = true}) => {
    const {i18n} = useContext(I18nReactContext);
    const [selectedOrganizations, setSelectedOrganizations] = useState(() => {
        const orgIds = get(filterModel, 'filterValues.organizationIds', []);
        const orgsData = get(filterModel, 'filterData.organizations', []);
        return orgsData.filter(org => orgIds.includes(org.value));
    });

    // Update selectedOrganizations when filterModel.filterData.organizations changes
    useEffect(() => {
        const orgIds = get(filterModel, 'filterValues.organizationIds', []);
        const orgsData = get(filterModel, 'filterData.organizations', []);
        const newSelectedOrgs = orgsData.filter(org => orgIds.includes(org.value));
        setSelectedOrganizations(newSelectedOrgs);
    }, [filterModel?.filterData?.organizations, filterModel?.filterValues?.organizationIds]);

    /**
     * Handles multiple organizations selection change (new multi-select mode).
     * When "all" is selected or no organizations selected, shows empty selection in UI
     * but sends 'all' in filter values to indicate no filtering.
     *
     * @param {Array} selectedOrgs - Array of selected organization objects with {value, text} format.
     */
    const onSelectOrgsChange = (selectedOrgs) => {
        // If user selects "all" option or deselects everything, show empty selection
        // but send 'all' to backend to indicate "no filter"
        const hasAllOption = selectedOrgs?.find(org => org.value === 'all');
        if (!selectedOrgs || selectedOrgs.length === 0 || hasAllOption) {
            setSelectedOrganizations([]); // Show empty in UI

            // Update parent immediately - use functional update to preserve filterData
            setFilterModel(prev => ({
                ...prev,
                filterValues: {
                    ...prev.filterValues,
                    organizationIds: ['all'], // Send 'all' to indicate no filtering
                }
            }));
            return;
        }

        setSelectedOrganizations(selectedOrgs);

        // Update parent immediately - use functional update to preserve filterData
        setFilterModel(prev => ({
            ...prev,
            filterValues: {
                ...prev.filterValues,
                organizationIds: selectedOrgs.map(org => org.value),
            }
        }));
    }

    /**
     * Filters organizations based on search query for multi-select autocomplete.
     * Separates search filtering from selection logic for proper UX.
     */
    const getFilteredOrgs = ({query}) => {
        const organizations = get(filterModel, 'filterData.organizations', []);
        
        // If no query, return full list so user can see all organizations after clearing search
        if (!query || query.trim().length === 0) {
            return organizations;
        }
        
        // Filter organizations based on search query
        return filter(organizations, org => 
            org.text.toLowerCase().includes(query.toLowerCase())
        );
    }

    // Render multi-select mode
    if (enableMultiSelect) {
        const organizations = get(filterModel, 'filterData.organizations', []);

        return (
            <div className="searchFilter">
                <MultiSelectAutocomplete
                    selectAllText={i18n.translate('editAdminUserModal.selectAllOrgs')}
                    enableSelectAll={enableSelectAll}
                    label={String(i18n.translate('allOrganizations'))}
                    fetchOptions={getFilteredOrgs}
                    initialSelectedValues={selectedOrganizations}
                    onSelectionChange={onSelectOrgsChange}
                    refreshTrigger={organizations.length}
                />
            </div>
        );
    }
}
